import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const html = event.detail.data.html;

    const newPrice = html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    // Find the matching product-price in the new HTML so we can also get its SKU element
    const newProductPrice = newPrice.closest('product-price');
    const newSku = newProductPrice ? newProductPrice.querySelector('.product-sku') : null;

    const currentSku = this.querySelector('.product-sku');

    // Update price
    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    // Update SKU (add, replace, or remove based on the new HTML)
    if (currentSku && newSku) {
      // Both exist: replace existing SKU with the new one
      currentSku.replaceWith(newSku);
    } else if (currentSku && !newSku) {
      // SKU no longer present for this variant: remove it
      currentSku.remove();
    } else if (!currentSku && newSku) {
      // SKU exists in new HTML but not currently rendered: insert it after the price
      const livePrice = this.querySelector('[ref="priceContainer"]');
      if (livePrice && livePrice.parentElement) {
        livePrice.parentElement.insertBefore(newSku, livePrice.nextSibling);
      } else {
        this.appendChild(newSku);
      }
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
